import "@/App.css";
import "@/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useState, createContext } from "react";
import axios from "axios";
import AIChatbot from "@/components/AIChatbot";
import { AnalyticsProvider, usePageViewTracking } from "@/components/AnalyticsProvider";

// Pages
import HomePage from "@/pages/HomePage";
import PropertyPage from "@/pages/PropertyPage";
import PropertiesPage from "@/pages/PropertiesPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import SellPage from "@/pages/SellPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import FavoritesPage from "@/pages/FavoritesPage";
import ComparePage from "@/pages/ComparePage";
import AlertsHistoryPage from "@/pages/AlertsHistoryPage";
import AlertsDashboardPage from "@/pages/AlertsDashboardPage";

// Contexts
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { CompareProvider } from "@/contexts/CompareContext";

// Components
import CompareBar from "@/components/CompareBar";

// Admin Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PropertyFormPage from "@/pages/admin/PropertyFormPage";
import BlogAdminPage from "@/pages/admin/BlogAdminPage";
import BlogFormPage from "@/pages/admin/BlogFormPage";
import ForgotPasswordPage from "@/pages/admin/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/admin/ResetPasswordPage";

// CRM Pages
import CRMDashboard from "@/pages/admin/crm/CRMDashboard";
import ContactsList from "@/pages/admin/crm/ContactsList";
import ContactForm from "@/pages/admin/crm/ContactForm";
import TeamPage from "@/pages/admin/crm/TeamPage";
import StagesPage from "@/pages/admin/crm/StagesPage";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";
import SetPasswordPage from "@/pages/admin/SetPasswordPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import MessagesPage from "@/pages/admin/MessagesPage";

// Context for global state
export const AppContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Conversion rates
const USD_TO_GTQ = 7.80;
const M2_TO_VARAS2 = 1.4310;

function App() {
  const [config, setConfig] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("USD"); // USD or GTQ
  const [areaUnit, setAreaUnit] = useState("m2"); // m2 or varas2

  // Fetch config and seed data on mount
  useEffect(() => {
    const initApp = async () => {
      try {
        // Get config
        const configRes = await axios.get(`${API}/config`);
        setConfig(configRes.data);

        // Seed demo data (only runs if empty)
        await axios.post(`${API}/seed`);

        // Get properties
        const propertiesRes = await axios.get(`${API}/properties`);
        setProperties(propertiesRes.data);
      } catch (error) {
        console.error("Error initializing app:", error);
      } finally {
        setLoading(false);
      }
    };

    initApp();
  }, []);

  // Conversion functions
  const convertPrice = (priceUSD) => {
    if (currency === "GTQ") {
      return priceUSD * USD_TO_GTQ;
    }
    return priceUSD;
  };

  const formatPrice = (priceUSD) => {
    const converted = convertPrice(priceUSD);
    if (currency === "GTQ") {
      return `Q${converted.toLocaleString("es-GT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return `$${converted.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const convertArea = (areaM2) => {
    if (areaUnit === "varas2") {
      return areaM2 * M2_TO_VARAS2;
    }
    return areaM2;
  };

  const formatArea = (areaM2) => {
    const converted = convertArea(areaM2);
    const unit = areaUnit === "varas2" ? "v²" : "m²";
    return `${converted.toLocaleString("es-GT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${unit}`;
  };

  const contextValue = {
    config,
    properties,
    setProperties,
    loading,
    currency,
    setCurrency,
    areaUnit,
    setAreaUnit,
    convertPrice,
    formatPrice,
    convertArea,
    formatArea,
    API
  };

  return (
    <HelmetProvider>
      <AnalyticsProvider>
        <FavoritesProvider>
          <CompareProvider>
            <AppContext.Provider value={contextValue}>
              <div className="App min-h-screen bg-[#050505]">
                <div className="noise-overlay" />
                <BrowserRouter>
                  <AppContent />
                  <CompareBar />
                </BrowserRouter>
                <Toaster position="top-right" theme="dark" />
              </div>
            </AppContext.Provider>
          </CompareProvider>
        </FavoritesProvider>
      </AnalyticsProvider>
    </HelmetProvider>
  );
}

// Separate component to use useLocation inside BrowserRouter
function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Track page views on route changes
  usePageViewTracking();

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/propiedades" element={<PropertiesPage />} />
        <Route path="/propiedad/:slug" element={<PropertyPage />} />
        <Route path="/nosotros" element={<AboutPage />} />
        <Route path="/contacto" element={<ContactPage />} />
        <Route path="/vender" element={<SellPage />} />
        <Route path="/comprar" element={<PropertiesPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/favoritos" element={<FavoritesPage />} />
        <Route path="/comparar" element={<ComparePage />} />
        <Route path="/mis-alertas" element={<AlertsHistoryPage />} />
        <Route path="/mis-alertas-dashboard" element={<AlertsDashboardPage />} />
        
        {/* Admin Routes - Ruta oculta para seguridad */}
        <Route path="/panel-gestion-8k2m/acceso" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/mensajes" element={<MessagesPage />} />
        <Route path="/admin/propiedades/nueva" element={<PropertyFormPage />} />
        <Route path="/admin/propiedades/:id" element={<PropertyFormPage />} />
        <Route path="/admin/blog" element={<BlogAdminPage />} />
        <Route path="/admin/blog/nuevo" element={<BlogFormPage />} />
        <Route path="/admin/blog/:id" element={<BlogFormPage />} />
        
        {/* CRM Routes */}
        <Route path="/admin/crm" element={<CRMDashboard />} />
        <Route path="/admin/crm/analytics" element={<AnalyticsPage />} />
        <Route path="/admin/crm/contacts" element={<ContactsList />} />
        <Route path="/admin/crm/contacts/new" element={<ContactForm />} />
        <Route path="/admin/crm/contacts/:id" element={<ContactForm />} />
        <Route path="/admin/crm/team" element={<TeamPage />} />
        <Route path="/admin/crm/stages" element={<StagesPage />} />
        
        {/* Auth Routes */}
        <Route path="/admin/set-password" element={<SetPasswordPage />} />
        <Route path="/admin/settings" element={<SettingsPage />} />
        <Route path="/panel-gestion-8k2m/recuperar" element={<ForgotPasswordPage />} />
        <Route path="/panel-gestion-8k2m/restablecer/:token" element={<ResetPasswordPage />} />
      </Routes>
      
      {/* AI Chatbot - only show on public pages */}
      {!isAdminRoute && <AIChatbot />}
    </>
  );
}

export default App;
